import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { FaApple, FaGooglePlay, FaStar, FaUsers, FaFilm, FaHeart } from 'react-icons/fa';
import './AppLanding.css';

function AppLanding() {
  const [isMobile, setIsMobile] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);

  useEffect(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    const mobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/.test(userAgent);
    const ios = /iphone|ipad|ipod/.test(userAgent);
    const android = /android/.test(userAgent);
    
    setIsMobile(mobile);
    setIsIOS(ios);
    setIsAndroid(android);
  }, []);

  const handleDownload = () => {
    if (isIOS) {
      window.location.href = 'https://apps.apple.com/fr/app/cin%C3%A9list/id6740208004?l=en-GB';
    } else if (isAndroid) {
      window.location.href = 'https://play.google.com/store/apps/details?id=com.cinemapp'; // Remplacer par l'ID réel
    } else {
      // Desktop: afficher les deux options
      document.getElementById('download-options').scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="app-landing">
      <Helmet>
        <meta 
          name="apple-itunes-app" 
          content="app-id=6740208004, app-argument=https://films.ahkapp.fr/" 
        />
        <title>CinéList - Films & Séries entre amis</title>
        <meta 
          name="description" 
          content="Découvrez, notez et partagez vos films et séries préférés avec vos amis. Obtenez des recommandations personnalisées basées sur vos goûts." 
        />
        <meta 
          property="og:title" 
          content="CinéList - Films & Séries entre amis" 
        />
        <meta 
          property="og:description" 
          content="Découvrez, notez et partagez vos films et séries préférés avec vos amis. Obtenez des recommandations personnalisées basées sur vos goûts." 
        />
      </Helmet>
      
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <div className="app-icon">
            <img src="/logo512.png" alt="CinemApp" className="app-logo" />
          </div>
          
          <h1 className="app-title">CinemApp</h1>
          <p className="app-subtitle">Films & Séries entre amis</p>
          
          <p className="hero-description">
            Découvrez, notez et partagez vos films et séries préférés avec vos amis. 
            Obtenez des recommandations personnalisées basées sur vos goûts.
          </p>

          <div className="hero-buttons">
            {isMobile ? (
              <button className="download-btn primary" onClick={handleDownload}>
                {isIOS && <FaApple />}
                {isAndroid && <FaGooglePlay />}
                {!isIOS && !isAndroid && <FaFilm />}
                <span>
                  {isIOS && 'Télécharger sur l\'App Store'}
                  {isAndroid && 'Télécharger sur Google Play'}
                  {!isIOS && !isAndroid && 'Télécharger l\'app'}
                </span>
              </button>
            ) : (
              <button className="download-btn primary" onClick={handleDownload}>
                <FaFilm />
                <span>Télécharger l'app</span>
              </button>
            )}
          </div>

          {/* Features Preview */}
          <div className="features-preview">
            <div className="feature-item">
              <FaStar className="feature-icon" />
              <span>Notez vos films</span>
            </div>
            <div className="feature-item">
              <FaUsers className="feature-icon" />
              <span>Partagez avec vos amis</span>
            </div>
            <div className="feature-item">
              <FaHeart className="feature-icon" />
              <span>Recommandations personnalisées</span>
            </div>
          </div>
        </div>

        {/* Phone Mockup */}
        <div className="phone-mockup">
          <div className="phone-frame">
            <div className="phone-screen">
              <img src="/logo192.png" alt="App Screenshot" className="screenshot" />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features">
        <div className="features-content">
          <h2>Pourquoi CinemApp ?</h2>
          
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon-large">
                <FaStar />
              </div>
              <h3>Évaluez et Découvrez</h3>
              <p>Notez vos films et séries pour créer votre profil cinématographique unique et découvrir de nouveaux contenus.</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon-large">
                <FaUsers />
              </div>
              <h3>Social et Amis</h3>
              <p>Suivez les activités de vos amis, partagez vos découvertes et découvrez leurs recommandations.</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon-large">
                <FaHeart />
              </div>
              <h3>IA Personnalisée</h3>
              <p>Notre intelligence artificielle analyse vos goûts pour vous proposer des recommandations parfaitement adaptées.</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon-large">
                <FaFilm />
              </div>
              <h3>Base de Données Complète</h3>
              <p>Accédez à une vaste collection de films et séries avec informations détaillées, casting et plateformes de streaming.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Download Section */}
      <section className="download-section" id="download-options">
        <div className="download-content">
          <h2>Téléchargez CinemApp</h2>
          <p>Disponible gratuitement sur iOS et Android</p>
          
          <div className="download-buttons">
            <a 
              href="https://apps.apple.com/fr/app/cin%C3%A9list/id6740208004?l=en-GB" 
              className="store-button ios"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaApple className="store-icon" />
              <div className="store-text">
                <span className="store-label">Télécharger sur</span>
                <span className="store-name">App Store</span>
              </div>
            </a>

            <a 
              href="https://play.google.com/store/apps/details?id=com.cinemapp"
              className="store-button android"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaGooglePlay className="store-icon" />
              <div className="store-text">
                <span className="store-label">Télécharger sur</span>
                <span className="store-name">Google Play</span>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="app-footer">
        <div className="footer-content">
          <div className="footer-logo">
            <img src="/logo192.png" alt="CinemApp" />
            <span>CinemApp</span>
          </div>
          <p>&copy; 2025 CinemApp. Films & Séries entre amis.</p>
        </div>
      </footer>
    </div>
  );
}

export default AppLanding;